/*
    What closing a composer should do with what is in it.

    Kept apart from the component so the decision can be read and tested on its own. It is a
    small rule, but it is the one standing between somebody and the message they just wrote, and
    it has three outcomes rather than the obvious two.

    Deliberately NOT a timer. An earlier version of this file saved on its own every few seconds;
    that was withdrawn because each save appends a new message and asks the server to erase the
    previous one, and the erase is not reliable (see the ledger: the server reports success and
    the old copy survives). Saving on purpose, once, keeps Drafts honest.

    The cost of that choice, stated plainly: a prompt only appears when somebody closes the
    window on purpose. A crashed tab or a session that expires mid-sentence still loses the text.
    beforeUnloadGuard below covers the ordinary case of closing the browser; nothing covers a
    crash.
*/

export interface ComposerState {
    /** Differs from what was last written to the server — or from the seed, if never written. */
    dirty: boolean;
    /** No recipient, no subject, no body: nothing anybody would want kept. */
    empty: boolean;
    /** A draft for this composer already exists on the server. */
    hasDraft: boolean;
}

export type CloseAction =
    /** Ask before doing anything — there is unsaved writing in here. */
    | 'ask'
    /** Emptied an existing draft: remove it rather than leaving a blank one behind. */
    | 'delete'
    /** Nothing to keep and nothing to clean up. */
    | 'close';

export function closeAction(s: ComposerState): CloseAction {
    // An emptied draft is not a question. Leaving a blank message in Drafts is nobody's intent,
    // and asking "save this empty thing?" is a prompt with no useful answer.
    if (s.empty) return s.hasDraft ? 'delete' : 'close';

    // Unchanged since it was last written: the server already has exactly this.
    if (!s.dirty) return 'close';

    return 'ask';
}

/**
 * Whether the browser should warn before the page goes.
 *
 * Only when there is something unsaved to lose. Asking on every close trains people to click
 * through the warning, which is how the one that mattered gets dismissed too.
 */
export function beforeUnloadGuard(s: ComposerState): boolean {
    return s.dirty && !s.empty;
}
